import { OptionProps } from "components/common/utlis/TypeAnnotations";
import secureLocalStorage from "react-secure-storage";
import { useEffect, useState } from "react";
import { Option } from "components/common/services/ValidationService";
import { timePeriods } from "./ManageReports";
import { selectAuth } from "features/auth/AuthSlice";
import { useSelector } from "react-redux";
import { AREA_MANAGER, CENTRAL_DATA_MANAGEMENT_MICROSERVICE, OFFICE_MANAGER, RECRUITER, ADMIN, SUPER_ADMIN } from "Constants";
import { ApiCall } from "components/common/services/ApiServices";
import * as ENDPOINTS from "routes/ApiEndpoints";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import { t } from "../translation/Translation";
import Calender from "components/common/molecules/Calender";
import { useNavigate } from "react-router-dom";
import { downloadAuthedFile } from "components/common/services/apiUtils";
import Button from "components/common/atoms/Button";
import Reset from "static/images/Reset";
import EncryptDecryptService from "components/common/services/EncryptDecryptService";
import { mapToSelect } from "components/common/utlis/MapToSelect";
import NoRecords from "components/common/atoms/NoRecords";
import RightArrow from "static/images/RightArrow";
import { createQueryParams, filterAndMerge, getQueryParamsFromRedux } from "components/common/utlis/HelperFunctions";
import { RootState } from "store";
import { useDispatch } from "react-redux";
import { clearDestinationPath, setDestinationPath } from "features/NavigationSlice";
import PaginationNew from "components/common/atoms/PaginationNew";
import MultiSelectAtom from "components/common/atoms/MultiSelectAtom";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import CustomNotify from "components/common/atoms/CustomNotify";
import PdfIcon from "static/images/Pdf";


interface SearchData {
  type: string;
  initialFetch?: boolean;
  office: OptionProps[];
  managers?: OptionProps[];
  recruiters?: number[];
  timePeriod?: Option | null;
  from?: Date | null;
  to?: Date | null;
  currentPage: number;
  recordsPerPage: number;
  orderByColumn: string;
  orderIn: "" | "asc" | "desc";
}

interface SearchOptions {
  offices: OptionProps[];
  managers: OptionProps[];
  recruiters: OptionProps[];
}

interface ReportRecord {
  office: string;
  userId: number | string;
  name: string;
  number: string;
  startDate: string;
  endDate: string;
  year: string;
  officeId: number;
  documentExists?: boolean;
}

// Initialize search data based on query params
export const defaultSearchData: SearchData = {
  type: "recruiter",
  initialFetch: true,
  office: [],
  timePeriod: {
    label: "Weekly",
    value: 1
  },
  recruiters: [],
  from: null,
  to: null,
  currentPage: 1,
  recordsPerPage: 10,
  orderByColumn: "",
  orderIn: "",
};

const ManageRecruiterReports: React.FC = () => {
  const authUser = useSelector(selectAuth);
  // Report PDF language must follow the application language switcher (Header.tsx writes
  // "site_lang"), not navigator.language - the browser locale is unrelated to the app language.
  const appLang = secureLocalStorage.getItem("site_lang") ?? "nl";
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({
    offices: [],
    managers: [],
    recruiters: [],
  });
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const getInitialType = () => {
    let type = "";
    if (authUser.role === RECRUITER) {
      type = "recruiter";
    } else if (authUser.role === OFFICE_MANAGER || authUser.role === AREA_MANAGER || authUser.role === ADMIN || authUser.role === SUPER_ADMIN) {
      type = "om";
    }
    // else if (authUser.role === ADMIN || authUser.role === OFFICE_MANAGER) {
    //   type = "teamLead"
    // }
    return type;
  }

  const destinationPath = useSelector(
    (state: RootState) => state.navigation.destinationPath
  );
  const queryParams = getQueryParamsFromRedux(destinationPath);

  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [timePeriod, setTimePeriod] = useState<Option | null>(null);
  const navigate = useNavigate();

  const [paginationData, setPaginationData] = useState({
    totalPages: 1,
    totalRecords: 1,
    cursor: null,
  });

  //advanced search
  const defaultAdvSearchData = {
    street: "",
  };

  const defaultTabData = {
    tabFieldName: "",
    tabFieldValue: getInitialType(),
  };

  const filteredSearchData = filterAndMerge(defaultSearchData, queryParams);
  const filteredAdvSearchData = filterAndMerge(
    defaultAdvSearchData,
    queryParams
  );
  const filteredTabData = filterAndMerge(defaultTabData, queryParams);

  // Initialize search data based on query params, merging with default values
  const [searchData, setSearchData] = useState<SearchData>({
    ...defaultSearchData,
    ...filteredSearchData,
  });

  const [advSearchData, setAdvSearchData] = useState({
    ...defaultAdvSearchData,
    ...filteredAdvSearchData,
  });

  const [activeTab, setActiveTab] = useState({
    ...defaultTabData,
    ...filteredTabData,
  });

  useEffect(() => {
    const filterData = { ...searchData, ...advSearchData, ...activeTab };
    const searchUrl = createQueryParams(filterData);
    dispatch(setDestinationPath(searchUrl));
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchData(filterData);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchData]);

  const fetchData = async (searchFilters = {}) => {
    const data = {
      ...searchFilters,
    };

    const response = await ApiCall.service(
      ENDPOINTS.FETCH_REPORTS,
      "POST",
      data,
      true,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );

    if (response?.status === 200) {
      setReports(response?.data?.reports?.records);
      setPaginationData(response?.data?.reports?.paginationData);
      setTimePeriod(searchData?.timePeriod ?? null);
      const managers = mapToSelect(response?.data?.managers ?? []);

      setSearchOptions((prevData) => ({
        ...prevData,
        recruiters: response?.data?.recruiters ?? [],
        managers: managers,
        offices: response?.data?.offices ?? [],
      }));
      setLoading(false);
    } else {
      CustomNotify({
        type: "error",
        message: t("Some error occured")
      });
    }
  };

  const handleDateChange = (date: Date | null, name: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      [name]: date,
      currentPage: 1,
    }));
  };

  const timePeriodLinks: { [key: number]: string } = {
    1: "/recruiter/weekly/report",
    2: "/recruiter/monthly/report",
  };
  const link =
    typeof timePeriod?.value === "number"
      ? timePeriodLinks[timePeriod.value] || null
      : null;

  const handlePageChange = (pageNumber: number) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: pageNumber,
    }));
  };

  const handleSelectChange = (selectedOption: any, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      fromDashboard: false,
      [fieldName]: selectedOption,
    }));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setSearchData((prev) => ({
      ...prev,
      recordsPerPage: newItemsPerPage,
      currentPage: 1,
    }));
  };

  const handleReset = async (e: React.FormEvent) => {
    dispatch(clearDestinationPath());
    setSearchData({ ...defaultSearchData, office: [] });
    setAdvSearchData(defaultAdvSearchData);
    setActiveTab(defaultTabData);
  };

  const handleButton = (item: any) => {
    navigate(`${link}?data=${encodeURIComponent(EncryptDecryptService.encryptData(JSON.stringify(item)))}`)
  }

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Reports",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <div className="row">
        <div className="col-md-2">
          <SelectWithSearch
            title={t("Office")}
            name="office"
            value={searchData.office}
            search={true}
            onChange={(e) => {
              handleSelectChange(e, "office");
            }}
            options={searchOptions.offices}
            isDisabled={authUser.role === RECRUITER}
            className="col-md-2"
            isTranslate={true}
          />
        </div>
        {/* <div className="col-md-2">
                    <SelectWithSearch
                        title={t("Office manager")}
                        name="managers"
                        value={searchData.managers}
                        search={true}
                        onChange={(e) => { handleSelectChange(e, "managers") }}
                        options={[]}
                        className="col-md-2"
                    />
                </div> */}
        {authUser.role !== RECRUITER &&
          <div className="col-md-2">
            <MultiSelectAtom
              title={t("Recruiter")}
              onChange={(selectedOption) =>
                handleSelectChange(selectedOption, "recruiters")
              }
              name="recruiters"
              options={searchOptions.recruiters ?? []}
              value={searchData.recruiters ?? []}
            />
          </div>
        }

        <div className="col-md-2">
          <SelectWithSearch
            title={t("Time period")}
            name="timePeriod"
            value={searchData.timePeriod}
            search={true}
            onChange={(e) => {
              handleSelectChange(e, "timePeriod");
            }}
            options={timePeriods}
            className="col-md-2"
            isSelectOption={false}
          />
        </div>
        <div className="col-md-2 position-relative">
          <Calender
            label={t("From")}
            selectedDate={searchData.from}
            isMandatory={false}
            name="from"
            onChange={handleDateChange}
            showMonthDropdown={false}
            showYearDropdown={false}
            minDate={new Date("01/01/2024")}
            maxDate={new Date()}
          />
        </div>
        <div className="col-md-2 position-relative">
          <Calender
            label={t("To")}
            selectedDate={searchData.to}
            onChange={handleDateChange}
            isMandatory={false}
            name="to"
            showMonthDropdown={false}
            showYearDropdown={false}
            minDate={new Date("01/01/2024")}
            maxDate={new Date()}
          />
        </div>
        <div className="col-2 text-end mt-34">
          <Button
            title=""
            type="submit"
            handleClick={handleReset}
            className="btn delete-btn rounded-3 shadow-none  me-3 px-3 reset-btn"
          >
            <span title={t("Reset")}>
              <Reset />
            </span>
          </Button>
        </div>
      </div>
      <div className="row">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr className="TableHeader">
                <th className="ps-lg-4">{t("Office")}</th>
                {authUser.role !== RECRUITER && <th>{t("Recruiter")}</th>}
                {timePeriod?.value === 2 ? (
                  <>
                    <th>{t("Month number")}</th>
                    <th>{t("Monthly date")}</th>
                  </>
                ) : (
                  <>
                    <th>{t("Week number")}</th>
                    <th>{t("Weekly date")}</th>
                  </>
                )}

                <th>{t("Year")}</th>
                {/* <AccessControl
                                    requiredPermissions={[{
                                        permission: "Follow-up",
                                        update: true,
                                    }]}
                                    actions={true}
                                > */}
                <th>{t("Action")}</th>
                {/* </AccessControl> */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SpinnerWrapper headerLength={authUser.role !== RECRUITER ? 7 : 6} />
              ) : (
                reports && reports.length > 0 ? (
                  reports.map((item: ReportRecord, index) => {
                    const user = {
                      officeId: item.officeId,
                      recruiterId: item.userId,
                      periodNumber: item.number,
                      year: item.year,
                    };

                    return (
                      <tr key={index}>
                        <td className="ps-lg-4 text-break">{item?.office}</td>
                        {authUser.role !== RECRUITER && <td>{item?.name}</td>}
                        <td>{item?.number}</td>
                        <td>{`${item?.startDate} to ${item?.endDate}`}</td>
                        <td>{item?.year}</td>
                        {link ? (
                          <td className="table-action-icons">
                            {/* CR 608: monthly reports (timePeriod 2) are always downloadable
                                (generated on demand when no approved PDF exists); weekly keeps
                                the documentExists gate. Previous condition: {(item.documentExists) && ( */}
                            {(timePeriod?.value === 2 || item.documentExists) && (
                              <a
                                className="marginRightPoint5"
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  downloadAuthedFile(
                                    `/api/report/download-pdf?data=${encodeURIComponent(JSON.stringify({
                                      ...user,
                                      userId: item.userId,
                                      type: "recruiter",
                                      reportType: timePeriod?.value === 1 ? "weekly" : "monthly",
                                      userRole: authUser.role || "ADMIN",
                                      appLangKey: appLang,
                                      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                                    }))}&name=report_${item.year}&lang=${appLang}&year=${item.year}`,
                                    `report_${item.year}.pdf`,
                                    process.env.REACT_APP_CDM_SERVICE
                                  );
                                }}
                                title={t("Download PDF")}
                              >
                                <PdfIcon />
                              </a>
                            )}
                            <span
                              className="border-0 me-2"
                              onClick={() => handleButton(user)}>
                              <RightArrow />
                            </span>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })
                ) : (
                  <NoRecords headerLength={10} message={t("No reports found")} />
                )
              )}
            </tbody>
          </table>
          {!loading && (
            <div className="pagination d-block">
              <PaginationNew
                currentPage={searchData.currentPage}
                totalPages={paginationData.totalPages}
                totalRecords={paginationData.totalRecords}
                recordsPerPage={searchData.recordsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                onPageChange={(newPage: any) => handlePageChange(newPage)}
              />
            </div>
          )}
        </div>
      </div>
    </AccessControl>
  );
};
export default ManageRecruiterReports;
